import { Activity, Battery, Cable, Gauge, Radio, Server, Thermometer, Zap } from 'lucide-react';
import { formatAgo, formatRelative, formatUptime } from '../../lib/format';
import { RECEIVER_SENSITIVITY_DBM, SIGNAL_LEVEL_BOUNDARIES, signalBand } from '@simpleacs/shared';
import { Card, Pill } from '../../components/ui';

/**
 * How this ONU is doing, in one place.
 *
 * State leads, because "is it up" is the question asked before any other and
 * the answer changes how the rest is read — a receive level on an ONU that is
 * offline is the last one anybody saw, not a measurement of now.
 *
 * Then the light. The four figures describing a PON link were spread across
 * separate tiles with the fleet's other statistics, which made the question an
 * engineer actually asks — is this link healthy, and where is the loss — into a
 * hunt. They belong together because they are read together: what the ONU
 * hears, what it sends, what the OLT hears back, and how far away it is.
 *
 * Both receive levels are coloured against the same thresholds the rest of the
 * platform uses, so "good" here means what it means everywhere else.
 */
/** The chassis's down-cause vocabulary, in words a support call uses. */
const DOWN_REASON: Record<string, string> = {
  los: 'loss of signal',
  losi: 'signal lost from this ONU',
  lofi: 'frame lost from this ONU',
  sfi: 'signal failed',
  dying_gasp: 'power lost',
  reboot: 'rebooted',
  deactivated: 'deactivated',
  reregister: 're-registered',
  other: 'other',
};

export function OpticsCard({
  state,
  stateTone,
  lastSeenAt,
  uptime,
  lastOfflineAt,
  lastOfflineReason,
  configState,
  rxPowerDbm,
  txPowerDbm,
  oltRxPowerDbm,
  distanceM,
  temperatureC,
  voltageV,
  biasMa,
  countdown,
  reading,
  trend,
}: {
  /** The ONU's state as the chassis words it, e.g. `working`, `los`. */
  state?: string;
  stateTone?: 'success' | 'warn' | 'danger' | 'neutral';
  lastSeenAt?: number | null;
  /** When it came up, and whether the record reaches back far enough to be sure. */
  uptime?: { since: number; atLeast: boolean } | null;
  /** The chassis's own record: when it last went down and why (ZTE and Huawei over SNMP). */
  lastOfflineAt?: number | null;
  lastOfflineReason?: string | null;
  /** The chassis's verdict on the delivered configuration (ZTE "Config state"). */
  configState?: 'success' | 'fail' | 'warning' | null;
  /** What the subscriber's ONU hears from the OLT. */
  rxPowerDbm: number | null;
  /** What the ONU transmits. */
  txPowerDbm: number | null;
  /** What the OLT hears back, upstream of the splitter. */
  oltRxPowerDbm: number | null;
  distanceM: number | null;
  /**
   * Optical-module diagnostics (DDM): transceiver temperature, supply voltage,
   * and laser bias. Null on chassis that do not report them (e.g. the C650, or
   * an ONU the sweep has not reached over SNMP) — the strip hides itself rather
   * than paint a row of dashes.
   */
  temperatureC?: number | null;
  voltageV?: number | null;
  biasMa?: number | null;
  /** Seconds until the next automatic read, when one is running. */
  countdown?: number | null;
  reading?: boolean;
  /** Light over time, drawn last: the figures above are now, this is history. */
  trend?: React.ReactNode;
}) {
  const rx = signalBand(rxPowerDbm);
  const oltRx = signalBand(oltRxPowerDbm);

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Activity className={`size-4 ${reading ? 'animate-pulse text-primary' : 'text-muted'}`} />
          General
          {countdown != null && (
            <span className="text-xs font-normal text-faint">
              {reading ? (
                'reading…'
              ) : (
                <>
                  next read in <span className="tabular-nums">{countdown}s</span>
                </>
              )}
            </span>
          )}
        </span>
      }
    >
      {/* Three across where there is room. This card now shares a row with
          Settings, so it is half width on a wide screen — the same three
          columns crammed into half the space is worse than two lines. */}
      <div className="grid divide-y divide-line sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
        {state && (
          <div className="px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-faint">
              <Radio className="size-4" />
              State
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span
                className={`text-xl font-semibold ${
                  stateTone === 'success'
                    ? 'text-success'
                    : stateTone === 'warn'
                      ? 'text-warn'
                      : stateTone === 'danger'
                        ? 'text-danger'
                        : 'text-ink'
                }`}
              >
                {state.replace('_', ' ')}
              </span>
            </div>
            {/* No meter here — state is not a measurement on a scale. The gap
                keeps the three columns aligned with each other. */}
            <p className="mt-1 text-[11px] text-faint">
              {lastSeenAt ? `last seen ${formatRelative(lastSeenAt)}` : 'never seen up'}
            </p>
            {/* Under last seen, because they answer adjacent questions: when did
                we last hear from it, and how long has this run lasted.
                `atLeast` is not decoration — the event record does not reach
                back forever, and "up 6 days" when the truth is "up at least 6
                days, we started watching then" is a number somebody would plan
                a site visit around. */}
            {lastOfflineAt != null && (
              <span className="text-xs text-faint" title="As the chassis itself records it — the cause the CLI shows under show gpon onu detail-info">
                · went down {formatAgo(lastOfflineAt)}
                {lastOfflineReason ? ` (${DOWN_REASON[lastOfflineReason] ?? lastOfflineReason})` : ''}
              </span>
            )}
            {configState && configState !== 'success' && (
              <span
                className={`text-xs ${configState === 'fail' ? 'text-danger' : 'text-warn'}`}
                title="The chassis's verdict on the configuration it delivered over OMCI to one of its own vendor's ONUs. A foreign ONT on a ZTE chassis carries no verdict here: what fails there is ZTE's proprietary extras, and the CPE's WAN state says whether the subscriber is configured."
              >
                · config {configState}
              </span>
            )}
            {uptime && (
              <p className="text-[11px] text-faint">
                {uptime.atLeast ? 'up at least ' : 'up '}
                {formatUptime((Date.now() - uptime.since) / 1000)}
              </p>
            )}
          </div>
        )}
        <Level
          icon={<Radio className="size-4" />}
          label="ONU receive"
          detail="what the subscriber's ONU hears"
          dbm={rxPowerDbm}
          band={rx}
        />
        <Level
          icon={<Server className="size-4" />}
          label="OLT receive"
          detail="what the OLT hears back, upstream of the splitter"
          dbm={oltRxPowerDbm}
          band={oltRx}
        />
      </div>

      {/* The two figures stack in one column beside the chart, each taking
          half its height — side by side they each spanned the chart's full
          height and trailed the empty space the operator circled. */}
      <div className="grid divide-y divide-line border-t border-line sm:grid-cols-2 sm:divide-y-0 sm:divide-x 2xl:grid-cols-[1fr_2fr]">
        <div className="grid grid-rows-2 divide-y divide-line">
          <Small
            icon={<Zap className="size-3.5" />}
            label="ONU transmit"
            value={txPowerDbm === null ? '—' : `${txPowerDbm.toFixed(2)} dBm`}
            hint="what the ONU sends"
          />
          <Small
            icon={<Cable className="size-3.5" />}
            label="Distance"
            value={distanceM === null ? '—' : formatDistance(distanceM)}
            hint={distanceM === null ? 'the chassis did not report a ranging' : 'fibre run, ranged by the OLT'}
          />
        </div>
        <div>{trend}</div>
      </div>

      {/* The transceiver's own vital signs, below the light it carries. These
          are what a rising loss or a flapping link is read against — a laser
          that is cooking, under-volted, or driving hard is failing whatever the
          receive level says. Hidden entirely when the chassis reports none of
          them, so an ONU with no DDM shows no emptier a card than before. */}
      {(temperatureC != null || voltageV != null || biasMa != null) && (
        <div className="grid divide-y divide-line border-t border-line sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
          <Small
            icon={<Thermometer className="size-3.5" />}
            label="Module temp"
            value={temperatureC == null ? '—' : `${temperatureC.toFixed(1)} °C`}
            hint="optical transceiver"
          />
          <Small
            icon={<Battery className="size-3.5" />}
            label="Supply voltage"
            value={voltageV == null ? '—' : `${voltageV.toFixed(2)} V`}
            hint="transceiver Vcc"
          />
          <Small
            icon={<Gauge className="size-3.5" />}
            label="Bias current"
            value={biasMa == null ? '—' : `${biasMa.toFixed(2)} mA`}
            hint="laser drive"
          />
        </div>
      )}
    </Card>
  );
}

/** Metres up to a kilometre, then kilometres — nobody says "4130 m". */
function formatDistance(metres: number): string {
  return metres < 1000 ? `${metres} m` : `${(metres / 1000).toFixed(2)} km`;
}

function Level({
  icon,
  label,
  detail,
  dbm,
  band,
}: {
  icon: React.ReactNode;
  label: string;
  detail: string;
  dbm: number | null;
  band: { label: string; tone: 'success' | 'warn' | 'danger' | 'neutral' };
}) {
  // The explanatory line is the tooltip now (2026-09-10, "30% shorter"):
  // the label, the number and the meter are what the eye reads; the sentence
  // is there on hover.
  return (
    <div className="px-4 py-2.5" title={detail}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-faint">
        {icon}
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span
          className={`text-xl font-semibold tabular-nums ${
            band.tone === 'success'
              ? 'text-success'
              : band.tone === 'warn'
                ? 'text-warn'
                : band.tone === 'danger'
                  ? 'text-danger'
                  : 'text-faint'
          }`}
        >
          {dbm === null ? '—' : dbm.toFixed(2)}
        </span>
        {dbm !== null && <span className="text-sm text-muted">dBm</span>}
        <Pill state tone={band.tone === 'neutral' ? 'neutral' : band.tone}>{band.label}</Pill>
      </div>
      {/* Where this reading sits in the usable range, so a number that is
          merely inside it still shows how much margin is left. */}
      <Meter dbm={dbm} tone={band.tone} />
    </div>
  );
}

/**
 * A GPON receiver works from about −8 dBm down to −28. The bar is that window,
 * so a reading near either end looks near the end rather than merely being a
 * number an operator has to know the limits of.
 *
 * Both ends come from the shared band table now. They were the only place in
 * the codebase that knew the −28 floor as a number rather than as a comment,
 * and a meter drawn to a different bottom than the pills are coloured by is a
 * bar that disagrees with the word printed beside it.
 */
function Meter({ dbm, tone }: { dbm: number | null; tone: string }) {
  if (dbm === null) return <div className="mt-2 h-1 rounded-full bg-surface-2" />;
  const floor = RECEIVER_SENSITIVITY_DBM;
  const ceiling = SIGNAL_LEVEL_BOUNDARIES.high;
  const pct = Math.max(0, Math.min(100, ((dbm - floor) / (ceiling - floor)) * 100));
  return (
    <div className="relative mt-2 h-1 overflow-hidden rounded-full bg-surface-2">
      <div
        className={`h-full rounded-full ${
          tone === 'success'
            ? 'bg-success'
            : tone === 'warn'
              ? 'bg-warn'
              : tone === 'danger'
                ? 'bg-danger'
                : 'bg-faint'
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * Transmit and distance, dressed like the receive levels beside them.
 *
 * They shared a row with the light chart and read as afterthoughts beside
 * it — the operator asked for the same size and green as every other healthy
 * reading, the figure centred in its box, the explanatory line at the foot.
 */
function Small({
  icon,
  label,
  value,
  hint,
  tone = 'success',
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  /**
   * `success` (the default) greens a present value the way transmit and
   * distance read as healthy-when-known. `ink` leaves it the plain text
   * colour: DDM figures are raw diagnostics, not pass/fail readings, so a laser
   * at 78 °C should not wear the same green as a good receive level.
   */
  tone?: 'success' | 'ink';
}) {
  const empty = value === '—';
  return (
    // Label and value at the top like the row above; the explanatory line
    // sinks to the bottom of whatever height the chart gives this row.
    // One compact block, the same anatomy as the receive cells above it —
    // the floated-middle version left the gaps the operator circled.
    <div className="px-4 py-2.5" title={hint}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-faint">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          empty ? 'text-faint' : tone === 'ink' ? 'text-ink' : 'text-success'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
